import { optimizedCompletion } from '@/lib/openai/speedOptimizer';
import { ApiError } from '@/lib/errors';

export interface ValidationResult {
  overall_score: number; // 0-100
  is_realistic: boolean;
  issues: ValidationIssue[];
  recommendations: string[];
  market_insights: MarketInsight[];
  summary: string;
}

export interface ValidationIssue {
  category: 'salary' | 'experience' | 'skills' | 'responsibilities' | 'qualifications' | 'benefits' | 'general';
  severity: 'low' | 'medium' | 'high';
  issue: string;
  suggestion: string;
  impact: string;
}

export interface MarketInsight {
  type: 'salary_range' | 'skill_demand' | 'experience_level' | 'location_factor' | 'industry_trend';
  insight: string;
  confidence: number; // 0-100
}

interface JobDescriptionData {
  jobTitle: string;
  company: string;
  location?: string;
  industry?: string;
  experience?: string;
  experienceLevel?: string;
  skills: string;
  responsibilities: string;
  qualifications?: string;
  benefits?: string;
  salary?: string;
  content?: string; // Full generated content
}

export async function validateJobDescription(jobData: JobDescriptionData): Promise<ValidationResult> {
  try {
    // Create a comprehensive validation prompt
    const prompt = createValidationPrompt(jobData);
    
    const response = await optimizedCompletion(
      'strategic-analysis',
      [
        {
          role: 'system',
          content: `You are an expert HR consultant and market analyst specializing in job market analysis and recruitment best practices. 
          
          Your role is to analyze job descriptions for:
          1. Market realism and expectations
          2. Skills-to-experience alignment
          3. Compensation reasonableness
          4. Industry standards compliance
          5. Candidate attraction potential
          6. Potential bias or exclusionary language
          7. Clarity and completeness
          
          Provide actionable, specific feedback that helps improve job descriptions and makes them more effective at attracting qualified candidates while being fair and realistic.
          
          Always respond with valid JSON only, no markdown formatting.`
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      { useCache: true }
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from AI');
    }

    // Clean the response to ensure it's valid JSON
    const cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    
    try {
      const validationResult: ValidationResult = JSON.parse(cleanedContent);
      
      // Validate the response structure
      if (!validationResult.overall_score || !Array.isArray(validationResult.issues) || !Array.isArray(validationResult.recommendations)) {
        throw new Error('Invalid validation result structure');
      }
      
      return validationResult;
    } catch (parseError) {
      console.error('JSON parsing error:', parseError);
      console.error('Raw content:', content);
      
      // Fallback validation result
      return {
        overall_score: 75,
        is_realistic: true,
        issues: [{
          category: 'general',
          severity: 'low',
          issue: 'Unable to perform detailed validation due to technical issue',
          suggestion: 'Please review the job description manually for market alignment',
          impact: 'May affect candidate attraction'
        }],
        recommendations: [
          'Review salary expectations against market standards',
          'Ensure skills requirements match experience level',
          'Verify benefits are competitive for the industry'
        ],
        market_insights: [],
        summary: 'Technical validation issue occurred. Please review manually.'
      };
    }
  } catch (error) {
    console.error('Job description validation error:', error);
    throw new ApiError(
      error instanceof Error 
        ? `Validation failed: ${error.message}` 
        : 'Failed to validate job description', 
      500
    );
  }
}

function createValidationPrompt(jobData: JobDescriptionData): string {
  return `Please analyze this job description for market realism, expectation alignment, and effectiveness. Provide a comprehensive validation report.

JOB DETAILS:
Title: ${jobData.jobTitle}
Company: ${jobData.company}
${jobData.location ? `Location: ${jobData.location}` : ''}
${jobData.industry ? `Industry: ${jobData.industry}` : ''}
${jobData.experience ? `Experience Required: ${jobData.experience}` : ''}
${jobData.experienceLevel ? `Experience Level: ${jobData.experienceLevel}` : ''}
${jobData.salary ? `Salary: ${jobData.salary}` : ''}

Skills Required:
${jobData.skills}

Responsibilities:
${jobData.responsibilities}

${jobData.qualifications ? `Qualifications:\n${jobData.qualifications}` : ''}

${jobData.benefits ? `Benefits:\n${jobData.benefits}` : ''}

${jobData.content ? `\nFull Job Description:\n${jobData.content}` : ''}

ANALYSIS REQUIREMENTS:
1. Evaluate if the required experience level matches the skills and responsibilities
2. Assess if the job requirements are realistic for the market
3. Check for potential bias or exclusionary language
4. Verify industry-standard alignment
5. Identify missing critical information
6. Suggest improvements for candidate attraction
7. Provide market insights where possible

Respond with JSON in this exact format:
{
  "overall_score": <number 0-100>,
  "is_realistic": <boolean>,
  "issues": [
    {
      "category": "<salary|experience|skills|responsibilities|qualifications|benefits|general>",
      "severity": "<low|medium|high>",
      "issue": "<specific issue description>",
      "suggestion": "<actionable suggestion>",
      "impact": "<potential impact on hiring>"
    }
  ],
  "recommendations": [
    "<specific actionable recommendation>"
  ],
  "market_insights": [
    {
      "type": "<salary_range|skill_demand|experience_level|location_factor|industry_trend>",
      "insight": "<market insight>",
      "confidence": <number 0-100>
    }
  ],
  "summary": "<brief overall assessment>"
}

Focus on being constructive and specific. Identify both strengths and areas for improvement.`;
}

// Quick validation for real-time feedback
export async function quickValidateJobDescription(jobData: Partial<JobDescriptionData>): Promise<{
  score: number;
  issues: string[];
  isValid: boolean;
}> {
  try {
    // Basic validation checks
    const issues: string[] = [];
    let score = 100;

    // Check for essential fields
    if (!jobData.jobTitle?.trim()) {
      issues.push('Job title is required');
      score -= 20;
    }

    if (!jobData.skills?.trim()) {
      issues.push('Skills section is required');
      score -= 20;
    }

    if (!jobData.responsibilities?.trim()) {
      issues.push('Responsibilities section is required');
      score -= 20;
    }

    // Check for realistic experience expectations
    if (jobData.experience && jobData.skills) {
      const skillCount = jobData.skills.split(',').length;
      const experienceMatch = jobData.experience.toLowerCase();
      
      if (experienceMatch.includes('entry') && skillCount > 8) {
        issues.push('Too many skills required for entry-level position');
        score -= 15;
      }
      
      if (experienceMatch.includes('senior') && skillCount < 3) {
        issues.push('Senior positions typically require more specialized skills');
        score -= 10;
      }
    }

    // Check for overly long requirements
    if (jobData.qualifications && jobData.qualifications.length > 1000) {
      issues.push('Qualifications section is very long - may discourage applicants');
      score -= 5;
    }

    return {
      score: Math.max(0, score),
      issues,
      isValid: issues.length === 0
    };
  } catch (error) {
    console.error('Quick validation error:', error);
    return {
      score: 0,
      issues: ['Validation error occurred'],
      isValid: false
    };
  }
} 